import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trash2, Check, X, AlertCircle, Save } from "lucide-react";
import { VersionDiffViewer } from "./VersionDiffViewer";
import type { HobbyItem } from "./useHobbiesManager";

interface HobbyEditFormProps {
  editingHobby: Partial<HobbyItem>;
  setEditingHobby: React.Dispatch<React.SetStateAction<Partial<HobbyItem> | null>>;
  onSave: () => void;
  onCancel: () => void;
  activeDraft?: any;
  onSaveDraft?: () => void;
  onDiscardDraft?: () => void;
  liveHobby?: any;
}

function HobbyFormFields({
  editingHobby,
  setEditingHobby,
}: {
  editingHobby: Partial<HobbyItem>;
  setEditingHobby: React.Dispatch<React.SetStateAction<Partial<HobbyItem> | null>>;
}) {
  return (
    <tbody className="divide-y divide-border/40 text-left">
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Title</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            value={editingHobby.title_en || ""}
            onChange={(e) => setEditingHobby((p) => ({ ...p!, title_en: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="e.g. Inception / A Thousand Splendid Suns"
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            value={editingHobby.title_tr || ""}
            onChange={(e) => setEditingHobby((p) => ({ ...p!, title_tr: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="örn. Başlangıç / Bin Muhteşem Güneş"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Subtitle/Author/Artist</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            value={editingHobby.subtitle_en || ""}
            onChange={(e) => setEditingHobby((p) => ({ ...p!, subtitle_en: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="e.g. Christopher Nolan / Khaled Hosseini"
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            value={editingHobby.subtitle_tr || ""}
            onChange={(e) => setEditingHobby((p) => ({ ...p!, subtitle_tr: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="örn. Christopher Nolan / Khaled Hosseini"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Category</td>
        <td colSpan={2} className="px-3 py-2">
          <Select name="category" value={editingHobby.category || "music"} onValueChange={(val) => setEditingHobby((p) => ({ ...p!, category: val as any }))}>
            <SelectTrigger id="category" className="w-full bg-background/50 border-border/30 focus:ring-1 focus:ring-brand-primary h-9 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="music">Music</SelectItem><SelectItem value="books">Books</SelectItem><SelectItem value="films">Films</SelectItem></SelectContent>
          </Select>
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Link (Optional)</td>
        <td colSpan={2} className="px-3 py-2">
          <Textarea
            placeholder="e.g. Spotify or IMDb link"
            value={editingHobby.link || ""}
            onChange={(e) => setEditingHobby((p) => ({ ...p!, link: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          />
        </td>
      </tr>
    </tbody>
  );
}

export function HobbyEditForm({
  editingHobby,
  setEditingHobby,
  onSave,
  onCancel,
  activeDraft,
  onSaveDraft,
  onDiscardDraft,
  liveHobby,
}: HobbyEditFormProps) {
  const diffFields = [
    { key: "title_en", label: "English Title" },
    { key: "title_tr", label: "Turkish Title" },
    { key: "subtitle_en", label: "English Subtitle" },
    { key: "subtitle_tr", label: "Turkish Subtitle" },
    { key: "category", label: "Category" },
    { key: "link", label: "Link" },
  ];

  return (
    <div className="p-5 border border-border/60 rounded-2xl bg-card/65 space-y-4 max-w-3xl text-left">
      <h5 className="font-bold text-base text-brand-primary">{editingHobby.id ? "Edit Hobby Item" : "Add Hobby Item"}</h5>

      {/* Staging Actions Panel */}
      <div className="flex flex-wrap justify-between items-center bg-muted/15 border border-border/50 rounded-xl p-3 gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold">
          <AlertCircle className="h-4.5 w-4.5 text-amber-500" />
          <span>
            {activeDraft
              ? "⚠️ Staged Staging Draft has unpublished modifications."
              : "✅ Working copy is synced to database."}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {activeDraft && onDiscardDraft && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDiscardDraft}
              className="text-destructive border-destructive/20 hover:bg-destructive/5 font-bold h-9 rounded-lg"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Discard Draft
            </Button>
          )}
          {onSaveDraft && (
            <Button
              type="button"
              size="sm"
              onClick={onSaveDraft}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-9 rounded-lg"
            >
              <Save className="h-4 w-4 mr-1.5" /> Save as Staging Draft
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-border/60 rounded-xl bg-card/30">
        <table className="min-w-full divide-y divide-border/40 text-sm">
          <thead className="bg-muted/40 font-semibold text-brand-secondary">
            <tr>
              <th className="px-4 py-2.5 text-left w-1/4 border-r border-border/40 text-xs uppercase tracking-wider">Field</th>
              <th className="px-4 py-2.5 text-left w-3/8 border-r border-border/40 text-xs uppercase tracking-wider">English (EN)</th>
              <th className="px-4 py-2.5 text-left w-3/8 text-xs uppercase tracking-wider">Turkish (TR)</th>
            </tr>
          </thead>
          <HobbyFormFields editingHobby={editingHobby} setEditingHobby={setEditingHobby} />
        </table>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <Button variant="outline" onClick={onCancel}><X className="h-4 w-4 mr-2" /> Cancel</Button>
        <Button onClick={onSave} className="bg-brand-primary text-white"><Check className="h-4 w-4 mr-2" /> Publish Live</Button>
      </div>

      {liveHobby && (
        <VersionDiffViewer
          live={liveHobby}
          draft={editingHobby}
          fieldsToCompare={diffFields}
        />
      )}
    </div>
  );
}

export default HobbyEditForm;
